import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faTrash, faEdit, faPlus, faSearch, 
  faChevronLeft, faChevronRight, faExclamation 
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getVehiculos, saveVehiculo, deleteVehiculo } from "../../../services/vehiculoservice";
import "./Vehiculo.css"; 

export default function Vehiculo() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemPorPagina] = useState(10);

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  const [form, setForm] = useState({
    idvehiculo: 0, placa: "", marca: "", modelo: "", capacidad: "", tipo: ""
  });
  const [loading, setLoading] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  // Uso de useCallback para evitar el warning de dependencias de useEffect
  const loadData = useCallback(async () => {
    try {
      const res = await getVehiculos();
      // ORDEN DESCENDENTE: Los IDs mayores aparecen primero
      const sortedData = (res || []).sort((a, b) => b.idvehiculo - a.idvehiculo);
      setData(sortedData);
    } catch (error) {
      toast.error("Error al conectar con el servidor");
    }
  }, []);

  useEffect(() => { 
    loadData(); 
  }, [loadData]);

  // --- FILTRADO ---
  const filteredData = data.filter(item =>
    item.placa?.toLowerCase().includes(search.toLowerCase()) ||
    item.marca?.toLowerCase().includes(search.toLowerCase()) ||
    item.modelo?.toLowerCase().includes(search.toLowerCase())
  );

  // --- PAGINACIÓN ---
  const indexUltimoItem = paginaActual * itemPorPagina;
  const indexPrimerItem = indexUltimoItem - itemPorPagina;
  const itemsActuales = filteredData.slice(indexPrimerItem, indexUltimoItem);
  const paginasTotales = Math.ceil(filteredData.length / itemPorPagina);

  const rangoPaginas = () => {
    let inicio = Math.max(1, paginaActual - 1);
    let fin = Math.min(paginasTotales, inicio + 2);
    if (fin - inicio < 2) inicio = Math.max(1, fin - 2);
    const paginas = [];
    for (let i = inicio; i <= fin; i++) { if(i > 0) paginas.push(i); }
    return paginas;
  };

  // --- ACCIONES ---
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.placa.trim() || !form.marca.trim()) {
      return toast.warning("Placa y Marca son obligatorios");
    }

    // Bloquear placa duplicada si es registro nuevo
    if (form.idvehiculo === 0) {
      const placaRepetida = data.some(v => v.placa.toUpperCase() === form.placa.toUpperCase());
      if (placaRepetida) return toast.error(`La placa ${form.placa} ya existe`);
    }
    
    setLoading(true);
    const res = await saveVehiculo(form);
    setLoading(false);

    if (res.res === 1) {
      toast.success("Guardado correctamente");
      setModalOpen(false);
      loadData();
    } else {
      toast.error(res.mensaje);
    }
  };

  const confirmDelete = async () => {
    const res = await deleteVehiculo(idToDelete);
    if (res.res === 1) {
      toast.success("Eliminado");
      loadData();
    } else {
      toast.error("No se pudo eliminar");
    }
    setDeleteModalOpen(false);
  };

  const openModal = (item = null) => {
    if (item) {
      setForm({ ...item });
    } else {
      setForm({ idvehiculo: 0, placa: "", marca: "", modelo: "", capacidad: "", tipo: "" });
    }
    setModalOpen(true);
  };

  return (
    <div className="main-content">
      <ToastContainer position="top-right" autoClose={2000} />
      
      <div className="nuevo-container">
        <div className="search-container">
          <div className="search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar vehículo..." 
              className="search-input"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPaginaActual(1); }}
            />
          </div>
        </div>
        <button className="nuevo" onClick={() => openModal(null)}>
          <FontAwesomeIcon icon={faPlus} /><span> Nuevo Vehículo</span>
        </button>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th className="col-id-header">ID</th>
              <th>Placa</th>
              <th>Marca</th>
              <th>Modelo</th>
              <th>Capacidad</th>
              <th>Tipo</th>
              <th className="col-actions-header">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {itemsActuales.length > 0 ? (
              itemsActuales.map((item) => (
                <tr key={item.idvehiculo}>
                  <td className="txt-id">{item.idvehiculo}</td>
                  <td className="txt-bold">{item.placa}</td>
                  <td>{item.marca}</td>
                  <td>{item.modelo}</td>
                  <td>{item.capacidad} asientos</td>
                  <td><span className="badge-tipo">{item.tipo}</span></td>
                  <td className="td-actions">
                    <button className="editar" onClick={() => openModal(item)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button className="eliminar" onClick={() => { setIdToDelete(item.idvehiculo); setDeleteModalOpen(true); }}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7" className="no-data">No hay datos</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-info">Página {paginaActual} de {paginasTotales || 1}</div>
        <div className="pagination-controls">
          <button className="pag-btn prev-next" onClick={() => setPaginaActual(p => Math.max(p - 1, 1))} disabled={paginaActual === 1}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          {rangoPaginas().map(num => (
            <button key={num} onClick={() => setPaginaActual(num)} className={`pag-btn number ${paginaActual === num ? 'active' : ''}`}>
              {num}
            </button>
          ))}
          <button className="pag-btn prev-next" onClick={() => setPaginaActual(p => Math.min(p + 1, paginasTotales))} disabled={paginaActual === paginasTotales || paginasTotales === 0}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* MODAL FORMULARIO */}
      {modalOpen && (
        <div className="modalOverlay" onClick={() => setModalOpen(false)}>
          <div className="modalWindow" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader">
              <h2>{form.idvehiculo === 0 ? "Registrar Vehículo" : "Actualizar Vehículo"}</h2>
              <button onClick={() => setModalOpen(false)} className="modalCloseX">×</button>
            </div>
            <div className="modalBody">
              <div className="modalRow">
                <div className="input-group">
                  <label>Placa</label>
                  <input className="input-modal" placeholder="ABC-123" value={form.placa} onChange={(e) => setForm({...form, placa: e.target.value.toUpperCase()})} />
                </div>
                <div className="input-group">
                  <label>Marca</label>
                  <input className="input-modal" placeholder="Ej: Toyota" value={form.marca} onChange={(e) => setForm({...form, marca: e.target.value})} />
                </div>
              </div>
              <div className="modalRow">
                <div className="input-group">
                  <label>Modelo</label>
                  <input className="input-modal" placeholder="Ej: Corolla" value={form.modelo} onChange={(e) => setForm({...form, modelo: e.target.value})} />
                </div>
                <div className="input-group">
                  <label>Capacidad (Asientos)</label>
                  <input className="input-modal" type="number" value={form.capacidad} onChange={(e) => setForm({...form, capacidad: e.target.value})} />
                </div>
              </div>
              <div className="input-group">
                <label>Tipo de Vehículo</label>
                <input className="input-modal" placeholder="Bus, Van, Auto" value={form.tipo} onChange={(e) => setForm({...form, tipo: e.target.value})} />
              </div>
            </div>
            <div className="modalFooter">
              <button onClick={() => setModalOpen(false)} className="modalBtn btnCancelar">Cancelar</button>
              <button onClick={handleSave} className="modalBtn btnGuardar" disabled={loading}>
                {loading ? "Procesando..." : "Guardar Registro"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAR */}
      {deleteModalOpen && (
        <div className="modalOverlay" onClick={() => setDeleteModalOpen(false)}>
          <div className="modalWindow deleteStyle" onClick={(e) => e.stopPropagation()}>
            <div className="deleteHeader"><button onClick={() => setDeleteModalOpen(false)} className="deleteCloseX">×</button></div>
            <div className="deleteIconContainer"><div className="deleteIconCircle"><FontAwesomeIcon icon={faExclamation} /></div></div>
            <div className="deleteBodyContent">
              <h3 className="deleteTitle">¿Eliminar registro?</h3>
              <p className="deleteText">Vehículo con placa: <b>{data.find(v => v.idvehiculo === idToDelete)?.placa}</b></p>
              <div className="deleteActions">
                <button onClick={() => setDeleteModalOpen(false)} className="btnCancelGhost">Cancelar</button>
                <button onClick={confirmDelete} className="btnConfirmFilled">Confirmar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}